import React, { useState, useRef, useEffect } from 'react'
import { Paper, useTheme, ButtonBase, Collapse, Box, Button } from '@mui/material'
import { alpha, keyframes } from '@mui/material/styles'
import QrCodeIcon from '@mui/icons-material/QrCode'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import StarIcon from '@mui/icons-material/Star'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import VerifiedIcon from '@mui/icons-material/Verified'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import Text from '../Text'
import metaWhite from '../../assets/meta-white-color.png'
import metaPrimary from '../../assets/meta-primary-color.png'
import DeviceDisconnected from './DeviceDisconnected'
import DevicePasskey from './DevicePasskey'
import ConnectTour from './ConnectTour'

const CLOUD = 'hiperapi-v3-cloud-official'
const API_VERSIONS_NEED_QRCODE = ['hiperapi-v1', 'hiperapi-v1-next', 'hiperapi-v2']
const SELECTOR_PROVIDERS = [...API_VERSIONS_NEED_QRCODE, CLOUD]
const VISITS_KEY = 'hs_conn_visits'

// conteúdo entra suave ao trocar de método
const reveal = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: none; }
`

// Comparativo do "Qual escolher?" — um card por método, cada traço com ícone
// (check = a favor · alerta = ressalva). Oficial em destaque.
const COMPARE = [
  {
    key: 'oficial',
    name: 'WhatsApp API Oficial',
    Icon: VerifiedIcon,
    recommended: true,
    traits: [
      { text: 'Oficial da Meta — sem risco de bloqueio' },
      { text: 'Sem QR e sem WhatsApp Web' },
      { text: 'Não precisa de app nem extensão' },
      { text: 'Operação profissional e durável' },
    ],
  },
  {
    key: 'key',
    name: 'Chave de acesso',
    Icon: VpnKeyIcon,
    traits: [
      { text: 'Sem QR — conecta pelo WhatsApp Web' },
      { text: 'Funciona mesmo com chave obrigatória' },
      { warn: true, text: 'Não-oficial: sujeito a bloqueio' },
      { warn: true, text: 'Precisa da extensão no navegador' },
    ],
  },
  {
    key: 'qr',
    name: 'QR Code',
    Icon: QrCodeIcon,
    traits: [
      { text: 'Rápido no dia a dia' },
      { text: 'Só o WhatsApp do celular' },
      { warn: true, text: 'Não-oficial: sujeito a bloqueio' },
      { warn: true, text: 'Não conecta com chave obrigatória' },
    ],
  },
]

// Seletor do método de conexão: WhatsApp API Oficial (destaque no topo) + QR Code e
// Chave de acesso (abaixo). Reaproveita DeviceDisconnected (QR e Cloud, via
// versionOverride) e DevicePasskey (wizard). O QR só é gerado quando o cliente
// escolhe QR. Traz o onboarding premium: destaque de 1ª visita, tour dispensável e
// o comparativo "Qual escolher?".
export default function DeviceMethodSelector({ isPasskeyRequired, provider, onQrCodeGenerated }) {
  const theme = useTheme()
  const primary = theme.palette.primary.main
  const warning = theme.palette.warning.main

  const [method, setMethod] = useState(provider === CLOUD ? 'oficial' : null) // null|'oficial'|'qr'|'key'
  const [compareOpen, setCompareOpen] = useState(false)
  const [spotlight, setSpotlight] = useState(false)
  const [tourOpen, setTourOpen] = useState(false)
  const [tourMandatory, setTourMandatory] = useState(false)

  const methodsRef = useRef(null)
  const oficialRef = useRef(null)
  const qrRef = useRef(null)
  const keyRef = useRef(null)
  const compareRef = useRef(null)

  const inSelector = SELECTOR_PROVIDERS.includes(provider)

  // Onboarding por contagem de acessos (lembrada no navegador): como muita coisa
  // mudou, os 2 primeiros acessos são guiados automaticamente (destaque + tour).
  //  • 1º acesso: tour OBRIGATÓRIO (sem "Pular" — o cliente passa pelos passos).
  //  • 2º acesso: tour automático, mas já dispensável.
  //  • 3º em diante: opcional (só pelo botão "Ver tour").
  useEffect(() => {
    if (!inSelector) return
    let visits = 0
    try { visits = parseInt(localStorage.getItem(VISITS_KEY) || '0', 10) || 0 } catch (e) { /* storage bloqueado */ }
    const thisVisit = visits + 1
    try { localStorage.setItem(VISITS_KEY, String(thisVisit)) } catch (e) { /* noop */ }
    if (thisVisit > 2) return
    if (thisVisit === 1) setTourMandatory(true)
    setSpotlight(true)
    const s = setTimeout(() => setSpotlight(false), 5200)
    const t = setTimeout(() => setTourOpen(true), 900)
    return () => { clearTimeout(s); clearTimeout(t) }
  }, [inSelector])

  // No cold load o provider chega DEPOIS do mount (o contexto busca com atraso),
  // então a pré-seleção do useState não pega cliente Meta — sincroniza aqui quando
  // ele chegar, sem atropelar uma escolha manual já feita.
  useEffect(() => {
    if (provider === CLOUD) setMethod((m) => m || 'oficial')
  }, [provider])

  // Provider ainda não carregou: segura o render (senão piscaria o fluxo legado).
  if (!provider) {
    return null
  }

  // Provedores fora do conjunto (ex.: superchats) vão direto ao fluxo padrão.
  if (!inSelector) {
    return <DeviceDisconnected onQrCodeGenerated={onQrCodeGenerated} />
  }

  const selected = isPasskeyRequired && method !== 'oficial' ? 'key' : method
  const qrDisabled = isPasskeyRequired
  const qrVersion = API_VERSIONS_NEED_QRCODE.includes(provider) ? provider : 'hiperapi-v1'

  const pulse = keyframes`
    0%   { box-shadow: 0 0 0 0 ${alpha(primary, 0.55)}; }
    70%  { box-shadow: 0 0 0 12px ${alpha(primary, 0)}; }
    100% { box-shadow: 0 0 0 0 ${alpha(primary, 0)}; }
  `

  // ---- card menor (QR / Chave) ----
  const smallCardSx = (active, disabled, pulsing) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
    height: '100%',
    p: '18px 16px',
    borderRadius: '12px',
    border: `1px solid ${active ? primary : theme.palette.divider}`,
    borderStyle: disabled ? 'dashed' : 'solid',
    backgroundColor: active ? alpha(primary, 0.07) : theme.palette.background.paper,
    boxShadow: active ? `0 0 0 2px ${primary}` : 'none',
    opacity: disabled ? 0.6 : 1,
    transition: 'transform .18s cubic-bezier(.2,.7,.3,1), box-shadow .2s ease, border-color .2s ease, background-color .2s ease',
    animation: pulsing ? `${pulse} 1.6s ease-out 3` : 'none',
    '&:hover': disabled ? {} : { transform: 'translateY(-3px)', boxShadow: theme.shadows[8], borderColor: active ? primary : alpha(primary, 0.5) },
    '&:active': disabled ? {} : { transform: 'translateY(-1px)' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none', '&:hover': { transform: 'none' } },
  })

  const iconSx = (active) => ({
    position: 'relative',
    width: 38,
    height: 38,
    borderRadius: '10px',
    display: 'grid',
    placeItems: 'center',
    mb: 1,
    bgcolor: active ? primary : theme.palette.action.hover,
    color: active ? '#fff' : theme.palette.text.secondary,
    transition: 'background-color .2s ease, color .2s ease',
  })

  const checkSx = (active) => ({
    position: 'absolute',
    top: -6,
    right: -6,
    width: 19,
    height: 19,
    borderRadius: '50%',
    bgcolor: primary,
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    boxShadow: theme.shadows[2],
    transform: active ? 'scale(1)' : 'scale(0)',
    transition: 'transform .2s cubic-bezier(.34,1.56,.64,1)',
  })

  // ---- card de destaque (Meta Oficial) ----
  const heroSx = (active, pulsing) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    textAlign: 'left',
    p: { xs: '16px', sm: '18px 22px' },
    borderRadius: '14px',
    border: `1px solid ${active ? primary : alpha(primary, 0.35)}`,
    background: `linear-gradient(135deg, ${alpha(primary, 0.12)} 0%, ${alpha(primary, 0.04)} 55%, ${theme.palette.background.paper} 100%)`,
    boxShadow: active ? `0 0 0 2px ${primary}` : 'none',
    transition: 'transform .18s cubic-bezier(.2,.7,.3,1), box-shadow .2s ease, border-color .2s ease',
    animation: pulsing ? `${pulse} 1.6s ease-out 3` : 'none',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[8], borderColor: primary },
    '&:active': { transform: 'translateY(-1px)' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none', '&:hover': { transform: 'none' } },
  })

  const badgeStyle = { display: 'inline-flex', alignItems: 'center', fontSize: '.66rem', fontWeight: 800, padding: '3px 8px', borderRadius: 20 }

  const tourSteps = [
    { getEl: () => methodsRef.current, title: 'Três jeitos de conectar', body: 'A API Oficial da Meta em destaque, e as opções não-oficiais (QR e Chave de acesso) logo abaixo.' },
    { getEl: () => oficialRef.current, title: 'WhatsApp API Oficial (recomendada)', body: 'Conexão oficial da Meta — mais estável e sem risco de bloqueio. É a nossa recomendação.' },
    { getEl: () => keyRef.current, title: 'Chave de acesso', body: 'Sem QR, pelo WhatsApp Web — funciona mesmo quando a conta exige chave de acesso.' },
    { getEl: () => compareRef.current, title: 'Na dúvida?', body: 'Abra “Qual escolher?” e compare as três opções lado a lado.' },
  ]

  const metaLogo = theme.isDark ? metaWhite : metaPrimary

  return (
    <div>
      <Paper elevation={5}>
        <Paper elevation={0}>
          <div className="p-5 sm:p-8 xl:p-10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Text className="text-xl md:text-2xl font-light leading-tight">Como conectar este número</Text>
                <Text className="mt-1" style={{ opacity: 0.7 }}>Escolha o método de conexão.</Text>
              </div>
              <Button size="small" color="inherit" startIcon={<HelpOutlineIcon />} onClick={() => { setTourMandatory(false); setTourOpen(true) }} sx={{ flexShrink: 0, opacity: 0.75 }}>
                Ver tour
              </Button>
            </div>

            <Box ref={methodsRef} sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* DESTAQUE — WhatsApp API Oficial */}
              <ButtonBase
                focusRipple
                ref={oficialRef}
                aria-pressed={selected === 'oficial'}
                onClick={() => setMethod('oficial')}
                sx={heroSx(selected === 'oficial', spotlight)}
              >
                <img src={metaLogo} alt="Meta" style={{ width: 78, flex: 'none' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex items-center flex-wrap gap-2">
                    <Text className="font-bold" style={{ fontSize: '1.05rem' }}>WhatsApp API Oficial</Text>
                    <span style={{ ...badgeStyle, color: primary, background: alpha(primary, 0.14) }}>
                      <StarIcon style={{ fontSize: 12, marginRight: 4 }} /> Recomendada
                    </span>
                    <span style={{ ...badgeStyle, color: theme.palette.text.secondary, background: theme.palette.action.hover, fontWeight: 700 }}>Oficial</span>
                  </div>
                  <Text className="text-sm mt-1" style={{ opacity: 0.8 }}>
                    Conexão oficial da Meta — mais estável e sem risco de bloqueio por uso não-oficial.
                  </Text>
                </div>
                {selected === 'oficial'
                  ? <CheckCircleIcon style={{ color: primary, flex: 'none' }} />
                  : <ChevronRightIcon style={{ opacity: 0.4, flex: 'none' }} />}
              </ButtonBase>

              {/* QR Code + Chave de acesso */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, alignItems: 'stretch' }}>
                <ButtonBase
                  focusRipple
                  ref={qrRef}
                  disabled={qrDisabled}
                  aria-pressed={selected === 'qr'}
                  onClick={() => { if (!qrDisabled) setMethod('qr') }}
                  sx={smallCardSx(selected === 'qr', qrDisabled, false)}
                >
                  <Box sx={iconSx(selected === 'qr')}>
                    <QrCodeIcon sx={{ fontSize: 22 }} />
                    <Box sx={checkSx(selected === 'qr')}><CheckIcon sx={{ fontSize: 13 }} /></Box>
                  </Box>
                  <Text className="font-bold">QR Code</Text>
                  {qrDisabled ? (
                    <span className="inline-flex items-center mt-2 px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: alpha(warning, 0.12), color: warning }}>
                      Esta conta exige chave de acesso
                    </span>
                  ) : (
                    <Text className="text-sm mt-1" style={{ opacity: 0.7 }}>Escaneie com o WhatsApp do aparelho.</Text>
                  )}
                </ButtonBase>

                <ButtonBase
                  focusRipple
                  ref={keyRef}
                  aria-pressed={selected === 'key'}
                  onClick={() => setMethod('key')}
                  sx={smallCardSx(selected === 'key', false, false)}
                >
                  <Box sx={iconSx(selected === 'key')}>
                    <VpnKeyIcon sx={{ fontSize: 22 }} />
                    <Box sx={checkSx(selected === 'key')}><CheckIcon sx={{ fontSize: 13 }} /></Box>
                  </Box>
                  <Text className="font-bold">Chave de acesso</Text>
                  <Text className="text-sm mt-1" style={{ opacity: 0.7 }}>Sem QR — funciona mesmo com chave de acesso obrigatória.</Text>
                  <span className="inline-flex items-center mt-2 px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: alpha(primary, 0.12), color: primary }}>Beta</span>
                </ButtonBase>
              </Box>
            </Box>

            {/* "Qual escolher?" — comparativo das 3 opções */}
            <div className="mt-4">
              <ButtonBase ref={compareRef} onClick={() => setCompareOpen((o) => !o)} sx={{ borderRadius: '8px', px: 1, py: 0.5, fontWeight: 600, fontSize: '.82rem', color: primary }}>
                Qual escolher?
                <ExpandMoreIcon sx={{ fontSize: 18, ml: 0.5, transition: 'transform .2s ease', transform: compareOpen ? 'rotate(180deg)' : 'none' }} />
              </ButtonBase>
              <Collapse in={compareOpen}>
                <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 2, alignItems: 'stretch' }}>
                  {COMPARE.map((m) => {
                    const highlight = m.key === 'oficial'
                    return (
                      <Box
                        key={m.key}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          p: 2,
                          borderRadius: '12px',
                          border: `1px solid ${highlight ? primary : theme.palette.divider}`,
                          backgroundColor: highlight ? alpha(primary, 0.05) : theme.palette.background.paper,
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <Box sx={{ width: 32, height: 32, borderRadius: '8px', flex: 'none', display: 'grid', placeItems: 'center', bgcolor: alpha(primary, highlight ? 0.16 : 0.1), color: primary }}>
                            <m.Icon sx={{ fontSize: 18 }} />
                          </Box>
                          <Text className="font-bold text-sm">{m.name}</Text>
                        </div>
                        {m.recommended && (
                          <span style={{ ...badgeStyle, alignSelf: 'flex-start', marginTop: 8, color: primary, background: alpha(primary, 0.14) }}>
                            <StarIcon style={{ fontSize: 11, marginRight: 4 }} /> Recomendada
                          </span>
                        )}
                        <div style={{ height: 1, background: theme.palette.divider, margin: '12px 0' }} />
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                          {m.traits.map((t, i) => (
                            <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                              {t.warn ? (
                                <WarningAmberIcon style={{ fontSize: 17, color: warning, flexShrink: 0, marginTop: 1 }} />
                              ) : (
                                <CheckCircleIcon style={{ fontSize: 17, color: primary, flexShrink: 0, marginTop: 1 }} />
                              )}
                              <Text className="text-sm" style={{ opacity: 0.85, lineHeight: 1.35 }}>{t.text}</Text>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )
                  })}
                </Box>
              </Collapse>
            </div>
          </div>
        </Paper>
      </Paper>

      {/* conteúdo do método escolhido, com reveal ao trocar */}
      <Box
        key={selected || 'none'}
        className="mt-8"
        sx={{ animation: `${reveal} .34s cubic-bezier(.2,.7,.3,1) both`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
      >
        {selected === 'oficial' && <DeviceDisconnected versionOverride={CLOUD} onQrCodeGenerated={onQrCodeGenerated} />}
        {/* QR só monta (e só então gera QR) quando o cliente escolhe QR */}
        {selected === 'qr' && <DeviceDisconnected versionOverride={qrVersion} onQrCodeGenerated={onQrCodeGenerated} />}
        {selected === 'key' && <DevicePasskey passkeyRequired={isPasskeyRequired} />}
        {selected == null && (
          <Paper elevation={0} style={{ border: `1px dashed ${theme.palette.divider}` }}>
            <div className="py-14 px-6 flex flex-col items-center text-center" style={{ opacity: 0.65 }}>
              <TipsAndUpdatesIcon style={{ fontSize: 32, color: primary }} />
              <Text className="mt-3 font-medium">Selecione um método acima para conectar</Text>
              <Text className="text-sm mt-1">O QR Code só é gerado quando você escolhe conectar por QR.</Text>
            </div>
          </Paper>
        )}
      </Box>

      <ConnectTour open={tourOpen} steps={tourSteps} onClose={() => setTourOpen(false)} mandatory={tourMandatory} />
    </div>
  )
}
